import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuthoringStore } from '../../store/UseAuthorStore';
import { CatalogFigure } from '../../models/MasterAssembly';
import { masterAssemblyApi } from '../../apiServices/masterassembly';
import {
  fetchLatestPartsTranslationsByMasterAssembly,
  type PartsTranslationLatestDto,
} from '../../apiServices/partsTranslationsApi';
import SVGViewer from './SVGViewer';
import CatalogList from './CatalogList';
import FigureList from './FigureList';
import ReviewCommentsPanel from '../reviewComments/ReviewCommentsPanel';
import { Typography } from '@mui/material';
import { useUserStore } from '../../store/userStore';
import { useCatalogLanguageStore } from '../../store/catalogLanguageStore';

const CATALOG_LANGUAGE_OPTIONS = [
  { code: 'fr', label: 'French (FR)' },
  { code: 'es', label: 'Spanish (ES)' },
  { code: 'de', label: 'German (DE)' },
  { code: 'it', label: 'Italian (IT)' },
  { code: 'pt', label: 'Portuguese (PT)' },
  { code: 'ja', label: 'Japanese (JA)' },
  { code: 'ko', label: 'Korean (KO)' },
  { code: 'zh', label: 'Chinese (ZH)' },
];

const getCatalogLanguageLabel = (code: string | null | undefined) => {
  const key = String(code || '').trim().toLowerCase();
  if (!key) return 'English';
  const match = CATALOG_LANGUAGE_OPTIONS.find((option) => option.code === key);
  return match ? match.label : key.toUpperCase();
};

const isCompletedTranslation = (row: PartsTranslationLatestDto) => {
  const status = String(row.latestQueueStatus || '').trim().toUpperCase();
  return Boolean(
    row.latestCompletedOn ||
      status === 'COMPLETED' ||
      status === 'SUCCESS'
  );
};

const ComponentDetailView: React.FC = () => {
  const params = useParams<{ oem?: string; masterAssemblyId?: string }>();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();

  const userOemId = useUserStore((s) => s.user?.oemId);
  const userRole = String(useUserStore((s) => s.user?.role || 'OEM')).toUpperCase();
  const fallbackOem = userOemId ? `Oem${userOemId}` : (import.meta as any)?.env?.VITE_DEFAULT_OEM ?? 'Oem1';

  const oem = (params.oem ?? search.get('oem') ?? fallbackOem).trim();
  const routeMasterAssemblyId = (params.masterAssemblyId ?? search.get('masterAssemblyId') ?? '').trim();
  const parsedMasterAssemblyId = Number(routeMasterAssemblyId);
  const parsedQueryFigId = Number(search.get('figId'));
  const queryFigId =
    Number.isFinite(parsedQueryFigId) && parsedQueryFigId > 0 ? parsedQueryFigId : null;
  const rawLang = String(search.get('lang') || '').trim().toLowerCase();
  const queryLang = rawLang.length > 0 ? rawLang : undefined;
  const parsedQueryItemNumber = Number(search.get('itemNumber'));
  const queryItemNumber =
    Number.isFinite(parsedQueryItemNumber) && parsedQueryItemNumber > 0
      ? parsedQueryItemNumber
      : undefined;

  const { setMasterAssembly, cards, mainSvgPath, mainSvgFileName, masterAssembly } = useAuthoringStore();
  const selectedCatalogLanguage = useCatalogLanguageStore((s) => s.selectedCatalogLanguage);
  const setSelectedCatalogLanguage = useCatalogLanguageStore((s) => s.setSelectedCatalogLanguage);

  const [selectedFigure, setSelectedFigure] = useState<CatalogFigure | null>(null);
  const [catalogLanguageRows, setCatalogLanguageRows] = useState<PartsTranslationLatestDto[]>([]);
  const [mainPreviewError, setMainPreviewError] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [commentsModalMode, setCommentsModalMode] = useState<'add' | 'review'>('review');
  const [commentAutoStartToken, setCommentAutoStartToken] = useState(0);
  const [commentFigIdInput, setCommentFigIdInput] = useState('');
  const [commentItemNumberInput, setCommentItemNumberInput] = useState('');
  const clearingDeepLinkRef = useRef(false);
  const isFigureSelected = selectedFigure !== null;
  const isVendorRole = userRole.includes('VENDOR');
  const isRevisionLocked = Number(masterAssembly?.workspaceAssembly?.statusId || 0) === 5011;
  const canShowCommentsActions = !isVendorRole && !isRevisionLocked;
  const effectiveCatalogLanguage = queryLang ?? selectedCatalogLanguage;

  const resolvedMasterAssemblyId =
    Number.isFinite(parsedMasterAssemblyId) && parsedMasterAssemblyId > 0
      ? parsedMasterAssemblyId
      : Number(masterAssembly?.masterAssemblyId || 0);

  const parsedCommentFigId = Number(commentFigIdInput);
  const popupFigId =
    Number.isFinite(parsedCommentFigId) && parsedCommentFigId > 0
      ? parsedCommentFigId
      : undefined;

  const parsedCommentItemNumber = Number(commentItemNumberInput);
  const popupItemNumber =
    Number.isFinite(parsedCommentItemNumber) && parsedCommentItemNumber > 0
      ? parsedCommentItemNumber
      : undefined;

  const activeCommentFigId = commentsModalMode === 'add' ? selectedFigure?.figId : popupFigId;
  const activeCommentItemNumber = commentsModalMode === 'add' ? undefined : popupItemNumber;
  const preselectedItemNumber =
    queryItemNumber && selectedFigure && (queryFigId == null || Number(selectedFigure.figId) === queryFigId)
      ? queryItemNumber
      : undefined;

  const availableFigureOptions = useMemo(() => {
    const seen = new Set<number>();
    return (cards || [])
      .filter((fig) => {
        const id = Number(fig.figId);
        if (!Number.isFinite(id) || id <= 0) return false;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map((fig) => {
        const id = Number(fig.figId);
        const figNo = String(fig.figNumber || '').trim();
        const figTitle = String(fig.figTitle || '').trim();
        const label = [figNo, figTitle].filter(Boolean).join(' - ') || `Figure ${id}`;
        return { id, label };
      });
  }, [cards]);

  const catalogLanguageOptions = useMemo(() => {
    const seen = new Set<string>();
    const options = [{ code: '', label: 'English' }];

    catalogLanguageRows.forEach((row) => {
      const code = String(row.languageCode || '').trim().toLowerCase();
      if (!code || seen.has(code) || !isCompletedTranslation(row)) return;
      seen.add(code);
      options.push({ code, label: getCatalogLanguageLabel(code) });
    });

    if (effectiveCatalogLanguage && !seen.has(effectiveCatalogLanguage)) {
      options.push({
        code: effectiveCatalogLanguage,
        label: getCatalogLanguageLabel(effectiveCatalogLanguage),
      });
    }

    return options;
  }, [catalogLanguageRows, effectiveCatalogLanguage]);

  const handleCatalogLanguageChange = (languageCode: string) => {
    const language = String(languageCode || '').trim().toLowerCase();
    setSelectedCatalogLanguage(language);

    const nextSearch = new URLSearchParams(search);
    if (language) {
      nextSearch.set('lang', language);
    } else {
      nextSearch.delete('lang');
    }
    setSearch(nextSearch, { replace: true });
  };

  const resolveMediaUrl = (path: string) => {
    const p = String(path || '').trim();
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;

    const baseUrl = String(process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');
    if (!baseUrl) return p;

    if (p.startsWith('/')) return `${baseUrl}${p}`;
    if (p.startsWith('media/')) return `${baseUrl}/${p}`;
    return `${baseUrl}/${p.replace(/^\/+/, '')}`;
  };

  const mainPreviewUrl = resolveMediaUrl(mainSvgPath);

  useEffect(() => {
    if (queryLang && queryLang !== selectedCatalogLanguage) {
      setSelectedCatalogLanguage(queryLang);
    }
  }, [queryLang, selectedCatalogLanguage, setSelectedCatalogLanguage]);

  useEffect(() => {
    let mounted = true;
    const loadCatalogLanguageRows = async () => {
      if (!Number.isFinite(parsedMasterAssemblyId) || parsedMasterAssemblyId <= 0) {
        setCatalogLanguageRows([]);
        return;
      }

      try {
        const rows = await fetchLatestPartsTranslationsByMasterAssembly(
          oem,
          parsedMasterAssemblyId
        );
        if (mounted) setCatalogLanguageRows(rows);
      } catch {
        if (mounted) setCatalogLanguageRows([]);
      }
    };

    void loadCatalogLanguageRows();
    return () => {
      mounted = false;
    };
  }, [oem, parsedMasterAssemblyId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let data;

        if (Number.isFinite(parsedMasterAssemblyId) && parsedMasterAssemblyId > 0) {
          const resp = await masterAssemblyApi.getMasterWithManualsByRole(
            oem,
            parsedMasterAssemblyId,
            userRole,
            effectiveCatalogLanguage || undefined
          );
          if (!resp.masterAssembly) throw new Error('Master assembly not found');
          data = resp.masterAssembly;
        } else if (routeMasterAssemblyId) {
          // Backward compatibility: if someone still opens old part-number style URL.
          data = await masterAssemblyApi.getByOEMAndPartNumber(oem, routeMasterAssemblyId);
        } else {
          throw new Error('Invalid master assembly id');
        }

        setMasterAssembly(data);
      } catch (error) {
        console.error('Error loading component data:', error);
      }
    };

    fetchData();
  }, [oem, parsedMasterAssemblyId, effectiveCatalogLanguage, routeMasterAssemblyId, userRole, setMasterAssembly]);

  useEffect(() => {
    if (!Array.isArray(cards) || cards.length === 0) return;

    if (clearingDeepLinkRef.current) {
      if (queryFigId == null) {
        clearingDeepLinkRef.current = false;
      }
      return;
    }

    if (queryFigId == null) return;

    const nextFigure = cards.find((f) => Number(f.figId) === queryFigId) ?? null;
    if (nextFigure && selectedFigure !== nextFigure) {
      setSelectedFigure(nextFigure);
    }
  }, [cards, queryFigId, selectedFigure]);

  useEffect(() => {
    if (!selectedFigure) return;
    if (!Array.isArray(cards) || cards.length === 0) return;

    const selectedFigureId = Number(selectedFigure.figId);
    if (!Number.isFinite(selectedFigureId) || selectedFigureId <= 0) return;

    const refreshedFigure =
      cards.find((f) => Number(f.figId) === selectedFigureId) ?? null;

    if (!refreshedFigure) {
      setSelectedFigure(null);
      return;
    }

    if (refreshedFigure !== selectedFigure) {
      setSelectedFigure(refreshedFigure);
    }
  }, [cards, selectedFigure]);

  useEffect(() => {
    setMainPreviewError(false);
  }, [mainSvgPath, isFigureSelected]);

  useEffect(() => {
    if (!canShowCommentsActions && isCommentsModalOpen) {
      setIsCommentsModalOpen(false);
    }
  }, [canShowCommentsActions, isCommentsModalOpen]);

  const handleBackToList = () => {
    clearingDeepLinkRef.current = true;
    setSelectedFigure(null);
    if (queryFigId != null || queryItemNumber !== undefined) {
      const nextSearch = new URLSearchParams(search);
      nextSearch.delete('figId');
      nextSearch.delete('itemNumber');
      setSearch(nextSearch, { replace: true });
    } else {
      clearingDeepLinkRef.current = false;
    }
  };

  const handleFigureSelect = (fig: CatalogFigure) => {
    setSelectedFigure(fig);
  };

  const openCommentsModal = (mode: 'add' | 'review') => {
    if (!canShowCommentsActions) return;
    if (mode === 'add' && !selectedFigure?.figId) return;
    if (mode === 'review') {
      setCommentFigIdInput(selectedFigure?.figId ? String(selectedFigure.figId) : '');
    }
    setCommentsModalMode(mode);
    setIsCommentsModalOpen(true);
    if (mode === 'add') {
      setCommentAutoStartToken((v) => v + 1);
    }
  };

  const closeCommentsModal = () => {
    setIsCommentsModalOpen(false);
  };

  return (
    <div className="cdv-page">
      <style>{`
        .cdv-page {
          --cdv-bg: #f7f9fc;
          --cdv-surface: #ffffff;
          --cdv-border: #e0e0e0;
          --cdv-text: #1a1a1a;
          --cdv-muted: #6b7280;
          --cdv-primary: #003366;
          --cdv-shadow: 0 10px 24px rgba(17, 24, 39, 0.08);

          width: min(1600px, calc(100% - 24px));
          margin: 0 auto;
          padding: 0;
          background: var(--cdv-bg);
          color: var(--cdv-text);
          display: flex;
          flex-direction: column;
          gap: 16px;
          min-height: calc(100vh - 84px + 75px);
          max-height: calc(100vh - 84px + 75px);
          overflow: auto;
        }

        .cdv-header {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 40px;
          flex: 0 0 auto;
        }

        .cdv-title {
          margin: 0;
          color: var(--cdv-text);
          font-size: 20px;
          font-weight: 600;
          letter-spacing: 0.01em;
          transform: translateX(-72px);
        }

        .cdv-back-btn {
          border: 1px solid var(--cdv-primary);
          color: #fff;
          background: var(--cdv-primary);
          border-radius: 6px;
          padding: 7px 14px;
          font-size: 13px;
          font-weight: 600;
          line-height: 1;
        }

        .cdv-back-btn:hover {
          background: #0f4d8a;
        }

        .cdv-comment-btn {
          border: 1px solid var(--cdv-primary);
          color: #fff;
          background: var(--cdv-primary);
          border-radius: 6px;
          padding: 7px 14px;
          font-size: 13px;
          font-weight: 600;
          line-height: 1;
        }

        .cdv-comment-btn:hover {
          background: #0f4d8a;
        }

        .cdv-comment-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .cdv-language {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: var(--cdv-text);
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
        }

        .cdv-language select {
          border: 1px solid var(--cdv-border);
          border-radius: 6px;
          background: #fff;
          color: var(--cdv-text);
          padding: 6px 8px;
          font-size: 13px;
          min-width: 130px;
        }

        .cdv-header-actions {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .cdv-main {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          align-items: stretch;
          gap: 16px;
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: 2px;
        }

        .cdv-panel {
          flex: 1 1 0;
          min-width: 480px;
          min-height: 0;
          background: var(--cdv-surface);
          border: 1px solid var(--cdv-border);
          border-radius: 10px;
          box-shadow: var(--cdv-shadow);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .cdv-panel-body {
          flex: 1 1 auto;
          min-height: 0;
          display: flex;
          width: 100%;
          overflow: hidden;
        }

        .cdv-svg-panel .cdv-panel-body > * {
          flex: 1 1 auto;
          min-height: 0;
          width: 100%;
        }

        .cdv-bom-panel .cdv-catalog-wrap {
          height: 100%;
          min-height: 0;
          width: 100%;
          overflow: hidden;
        }

        .cdv-bom-panel .container.my-2 {
          margin: 0 !important;
          max-height: 100% !important;
          height: 100% !important;
          padding: 0 !important;
          border: 0 !important;
        }

        .cdv-bom-panel .card {
          height: 100% !important;
          border: 0 !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          display: flex;
          flex-direction: column;
        }

        .cdv-bom-panel .card-body {
          flex: 1 1 auto !important;
          min-height: 0 !important;
          padding: 0 !important;
          overflow: hidden;
          display: flex;
        }

        .cdv-bom-panel .ag-theme-alpine {
          height: 100% !important;
          width: 100% !important;
        }

        .cdv-list-panel .cdv-panel-body {
          overflow: auto;
          padding: 16px;
        }

        .cdv-preview-wrap {
          width: 100%;
          height: 100%;
          min-height: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: #f8fafc;
        }

        .cdv-preview-img {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          background: #fff;
        }

        .cdv-empty {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          color: var(--cdv-muted);
        }

        .cdv-main::-webkit-scrollbar {
          height: 8px;
        }

        .cdv-main::-webkit-scrollbar-thumb {
          background: #cdd6e5;
          border-radius: 999px;
        }

        .cdv-comments-overlay {
          position: fixed;
          inset: 0;
          z-index: 1200;
          background: rgba(15, 23, 42, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
        }

        .cdv-comments-modal {
          width: min(980px, 100%);
          max-height: min(88vh, 900px);
          background: #fff;
          border: 1px solid var(--cdv-border);
          border-radius: 10px;
          box-shadow: 0 20px 44px rgba(15, 23, 42, 0.2);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .cdv-comments-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          border-bottom: 1px solid var(--cdv-border);
          background: #f8fafc;
        }

        .cdv-comments-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .cdv-comments-filters {
          display: grid;
          grid-template-columns: repeat(3, minmax(140px, 1fr));
          gap: 10px;
          padding: 12px 16px;
          border-bottom: 1px solid var(--cdv-border);
          background: #fff;
        }

        .cdv-filter-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .cdv-filter-label {
          font-size: 12px;
          color: #4b5563;
          font-weight: 600;
        }

        .cdv-filter-input {
          border: 1px solid #d1d5db;
          border-radius: 6px;
          padding: 6px 8px;
          font-size: 13px;
          width: 100%;
        }

        .cdv-filter-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        .cdv-filter-input[readonly] {
          background: #f8fafc;
          color: #1f2937;
          cursor: default;
        }

        .cdv-filter-clear-wrap {
          display: flex;
          align-items: end;
          justify-content: flex-start;
        }

        .cdv-filter-clear-btn {
          border: 1px solid var(--cdv-primary);
          background: var(--cdv-primary);
          color: #fff;
          border-radius: 6px;
          padding: 6px 10px;
          font-size: 12px;
          font-weight: 600;
          line-height: 1;
        }

        .cdv-filter-clear-btn:hover {
          background: #0f4d8a;
        }

        .cdv-comments-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow: auto;
          padding: 12px 16px 16px;
        }

        .cdv-comments-empty {
          padding: 14px 2px;
          color: var(--cdv-muted);
        }

        @media (max-width: 1399.98px) {
          .cdv-header {
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 12px;
          }

          .cdv-header .cdv-title {
            transform: none;
            flex: 0 1 auto;
          }

          .cdv-header-actions {
            position: static;
            transform: none;
            flex-wrap: wrap;
            justify-content: flex-end;
            margin-left: auto;
            max-width: 100%;
          }

          .cdv-language select,
          .cdv-header-actions button {
            min-height: 44px;
          }
        }

        @media (max-width: 1100px) {
          .cdv-page {
            width: calc(100% - 12px);
            padding: 0;
            min-height: calc(100vh - 72px + 67px);
            max-height: calc(100vh - 72px + 67px);
          }

          .cdv-panel {
            min-width: 420px;
          }

          .cdv-title {
            font-size: 18px;
            transform: translateX(-32px);
          }

          .cdv-comments-filters {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="cdv-header">
        <h4 className="cdv-title">Component Detail View</h4>
        <div className="cdv-header-actions">
          <label className="cdv-language">
            <span>Language</span>
            <select
              value={effectiveCatalogLanguage || ''}
              onChange={(event) => handleCatalogLanguageChange(event.target.value)}
            >
              {catalogLanguageOptions.map((option) => (
                <option key={option.code || 'en'} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {canShowCommentsActions ? (
            <>
              {isFigureSelected ? (
                <button
                  className="cdv-comment-btn"
                  type="button"
                  onClick={() => openCommentsModal('add')}
                >
                  Add Comment
                </button>
              ) : null}
              <button className="cdv-comment-btn" type="button" onClick={() => openCommentsModal('review')}>
                Review Comments
              </button>
            </>
          ) : null}
          {isFigureSelected ? (
            <button className="cdv-back-btn" type="button" onClick={handleBackToList}>
              Back to Figure List
            </button>
          ) : (
            <button className="cdv-back-btn" type="button" onClick={() => navigate(-1)}>
              Back
            </button>
          )}
        </div>
      </div>

      {isFigureSelected ? (
        <div className="cdv-main">
          <section className="cdv-panel cdv-svg-panel">
            <div className="cdv-panel-body">
              <SVGViewer
                svgFiles={selectedFigure?.svgFiles ?? []}
                mainSvgPath={mainSvgPath}
                mainSvgFileName={mainSvgFileName}
                preselectedItemNumber={preselectedItemNumber}
              />
            </div>
          </section>

          <section className="cdv-panel cdv-bom-panel">
            <div className="cdv-panel-body">
              {Array.isArray(selectedFigure?.bomJson) ? (
                <div className="cdv-catalog-wrap">
                  <CatalogList
                    bomJson={selectedFigure.bomJson.map((item) => ({
                      itemNumber: item.itemNumber,
                      partNumber: item.partNumber,
                      description: item.description,
                      qty: item.qty,
                      uom: item.uom ?? null,
                      price: item.price == null ? null : Number(item.price),
                      partId: item.partId ?? null,
                      figId: selectedFigure.figId,
                    }))}
                    preselectedItemNumber={preselectedItemNumber}
                    layoutVariant="componentDetail"
                  />
                </div>
              ) : (
                <div className="cdv-empty">
                  <Typography variant="body2">No BOM data available</Typography>
                </div>
              )}
            </div>
          </section>
        </div>
      ) : (
        <div className="cdv-main">
          <section className="cdv-panel cdv-svg-panel">
            <div className="cdv-panel-body">
              <div className="cdv-preview-wrap">
                {mainPreviewUrl && !mainPreviewError ? (
                  <img
                    className="cdv-preview-img"
                    src={mainPreviewUrl}
                    alt={mainSvgFileName || 'Main figure preview'}
                    onError={() => setMainPreviewError(true)}
                  />
                ) : (
                  <Typography variant="body2">No main figure preview available</Typography>
                )}
              </div>
            </div>
          </section>

          <section className="cdv-panel cdv-list-panel">
            <div className="cdv-panel-body">
              <FigureList figures={cards} onSelectFigure={handleFigureSelect} />
            </div>
          </section>
        </div>
      )}

      {isCommentsModalOpen ? (
        <div className="cdv-comments-overlay" role="dialog" aria-modal="true" onClick={closeCommentsModal}>
          <div className="cdv-comments-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cdv-comments-header">
              <h5 className="cdv-comments-title">
                {commentsModalMode === 'add' ? 'Add Comment' : 'Review Comments'}
              </h5>
              <button className="cdv-back-btn" type="button" onClick={closeCommentsModal}>
                Close
              </button>
            </div>

            <div className="cdv-comments-filters">
              {commentsModalMode === 'add' ? (
                <>
                  <div className="cdv-filter-field">
                    <label className="cdv-filter-label" htmlFor="cdv-comment-fig-number">
                      Figure No
                    </label>
                    <input
                      id="cdv-comment-fig-number"
                      className="cdv-filter-input"
                      type="text"
                      readOnly
                      value={selectedFigure?.figNumber || '-'}
                    />
                  </div>

                  <div className="cdv-filter-field">
                    <label className="cdv-filter-label" htmlFor="cdv-comment-fig-title">
                      Figure Title
                    </label>
                    <input
                      id="cdv-comment-fig-title"
                      className="cdv-filter-input"
                      type="text"
                      readOnly
                      value={selectedFigure?.figTitle || '-'}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="cdv-filter-field">
                    <label className="cdv-filter-label" htmlFor="cdv-comment-figid">
                      Figure
                    </label>
                    <select
                      id="cdv-comment-figid"
                      className="cdv-filter-input"
                      value={commentFigIdInput}
                      onChange={(e) => setCommentFigIdInput(e.target.value)}
                    >
                      <option value="">All Figures</option>
                      {availableFigureOptions.map((opt) => (
                        <option key={opt.id} value={String(opt.id)}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="cdv-filter-field">
                    <label className="cdv-filter-label" htmlFor="cdv-comment-itemno">
                      Item Number
                    </label>
                    <input
                      id="cdv-comment-itemno"
                      className="cdv-filter-input"
                      type="number"
                      min={1}
                      value={commentItemNumberInput}
                      onChange={(e) => setCommentItemNumberInput(e.target.value)}
                      placeholder="Optional"
                    />
                  </div>

                  <div className="cdv-filter-clear-wrap">
                    <button
                      className="cdv-filter-clear-btn"
                      type="button"
                      onClick={() => {
                        setCommentFigIdInput('');
                        setCommentItemNumberInput('');
                      }}
                    >
                      Clear Filters
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="cdv-comments-body">
              {resolvedMasterAssemblyId > 0 && (commentsModalMode === 'review' || selectedFigure?.figId) ? (
                <ReviewCommentsPanel
                  domain="parts"
                  masterAssemblyId={resolvedMasterAssemblyId}
                  figId={activeCommentFigId}
                  itemNumber={activeCommentItemNumber}
                  autoStartAddToken={commentsModalMode === 'add' ? commentAutoStartToken || undefined : undefined}
                />
              ) : (
                <div className="cdv-comments-empty">
                  {selectedFigure?.figId
                    ? 'Comments unavailable. Invalid master assembly id.'
                    : 'Select a figure to add comments.'}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ComponentDetailView;
